import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { api, FalhaDaApi } from '../api/cliente';
import { Alerta, Botao } from './base';
import { AreaDeTexto, Caixa, Campo, Seletor } from './campos';
import { Modal } from './Modal';
import { moeda, plural } from '../util/formato';

const FORMAS = [
  { valor: 'PIX', rotulo: 'PIX' },
  { valor: 'BOLETO', rotulo: 'Boleto' },
  { valor: 'TRANSFERENCIA', rotulo: 'Transferência' },
  { valor: 'CARTAO', rotulo: 'Cartão' },
  { valor: 'DINHEIRO', rotulo: 'Dinheiro' },
  { valor: 'OUTRO', rotulo: 'Outro' },
];

interface ParcelaSimulada {
  readonly numero: number;
  readonly valorFormatado: string;
  readonly vencimentoFormatado: string;
  readonly ehEntrada: boolean;
}

interface Simulacao {
  readonly parcelas: ParcelaSimulada[];
  readonly confere: boolean;
}

/** "1.850,00" a partir de centavos — o formato em que se digita. */
function emReais(centavos: number): string {
  return (centavos / 100).toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Firmar acordo.
 *
 * A ficha oferecia "Firmar acordo" desde sempre, mas o botão não tinha ação: a rota
 * existia na API e a tela nunca foi construída. Sem ela, um devedor que aceitava
 * parcelar não tinha como virar acordo no sistema.
 *
 * O CORAÇÃO DAQUI É A SIMULAÇÃO. As parcelas aparecem antes de gravar, com data e
 * valor de cada uma, porque parcelamento tem arredondamento: R$ 1.000,00 em 3x não
 * dá três parcelas iguais, e quem combina com o devedor precisa ver onde foi parar
 * o centavo antes de prometer. A API calcula; a tela só mostra.
 */
/** O acordo que está sendo substituído, quando o diálogo abre em renegociação. */
export interface AcordoASubstituir {
  readonly id: string;
  readonly valorTotal: number;
  readonly totalPago: number;
  /**
   * Soma das parcelas EM ABERTO — as que serão canceladas e reescritas.
   *
   * NÃO é o "saldo" do cartão, que é `valorTotal - totalPago`. Os dois divergem
   * quando uma parcela foi baixada por menos do que valia: o sistema dá aquela
   * parcela por quitada ("a diferença não vira saldo a cobrar"), e a diferença
   * fica de fora do que ainda é devido. Partir do saldo do cartão recobraria essa
   * diferença dentro do acordo novo, sem ninguém perceber.
   */
  readonly saldoEmAberto: number;
  readonly parcelasPagas: number;
}

export function ModalFirmarAcordo({
  aberto,
  aoFechar,
  devedorId,
  nomeDoDevedor,
  valorSugerido,
  substituindo,
}: {
  aberto: boolean;
  aoFechar: () => void;
  devedorId: string;
  nomeDoDevedor: string;
  /** Dívida de origem, em centavos — o ponto de partida da negociação. */
  valorSugerido: number;
  /**
   * Presente, o diálogo RENEGOCIA em vez de firmar.
   *
   * É o mesmo formulário de propósito: renegociar é firmar um acordo novo, com os
   * mesmos campos. Um segundo formulário de dinheiro divergiria do primeiro na
   * primeira vez que um dos dois ganhasse um campo.
   */
  substituindo?: AcordoASubstituir | undefined;
}) {
  /* Por instância, não constante de módulo: dois diálogos iguais montados na mesma
     tela dividiriam o id, e o `form=` do botão do rodapé resolve pelo primeiro. */
  const ID_DO_FORMULARIO = useId();

  const clienteConsulta = useQueryClient();

  const [valorTotal, setValorTotal] = useState('');
  const [valorEntrada, setValorEntrada] = useState('');
  const [quantidadeParcelas, setQuantidadeParcelas] = useState('1');
  const [dataPrimeiraParcela, setDataPrimeiraParcela] = useState('');
  const [dataEntrada, setDataEntrada] = useState('');
  const [formaPagamento, setFormaPagamento] = useState('PIX');
  const [temMulta, setTemMulta] = useState(false);
  const [percentualMulta, setPercentualMulta] = useState('20');
  const [observacoes, setObservacoes] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [invalidos, setInvalidos] = useState<Record<string, string[]>>({});

  useEffect(() => {
    /* Firmando, o total nasce na dívida de origem: acordar por menos é comum, por
       mais quase nunca, e partir do valor cheio deixa a concessão explícita.

       Renegociando, nasce no SALDO EM ABERTO — o que já foi pago está pago, e
       recomeçar do valor cheio cobraria duas vezes a parcela quitada. Os juros da
       repactuação entram por cima, digitados por quem negociou. */
    const partida = substituindo ? substituindo.saldoEmAberto : valorSugerido;
    setValorTotal(partida > 0 ? emReais(partida) : '');
    setValorEntrada('');
    setQuantidadeParcelas('1');
    const proximoMes = new Date();
    proximoMes.setMonth(proximoMes.getMonth() + 1);
    setDataPrimeiraParcela(proximoMes.toISOString().slice(0, 10));
    setDataEntrada('');
    setFormaPagamento('PIX');
    setTemMulta(false);
    setPercentualMulta('20');
    setObservacoes('');
    setErro(null);
    setInvalidos({});
  }, [aberto, valorSugerido, substituindo]);

  /* A simulação acompanha o que está sendo digitado. É a razão de existir da tela:
     ver as parcelas antes de combinar com o devedor. */
  const podeSimular =
    valorTotal.trim() !== '' && quantidadeParcelas !== '' && dataPrimeiraParcela !== '';

  const { data: simulacao } = useQuery({
    queryKey: [
      'acordo-simulacao',
      valorTotal,
      valorEntrada,
      quantidadeParcelas,
      dataPrimeiraParcela,
      dataEntrada,
    ],
    enabled: aberto && podeSimular,
    queryFn: () =>
      api.criar<Simulacao>('/acordos/simular', {
        valorTotal,
        ...(valorEntrada.trim() ? { valorEntrada } : {}),
        quantidadeParcelas,
        dataPrimeiraParcela,
        ...(dataEntrada ? { dataEntrada } : {}),
      }),
    retry: false,
  });

  const firmar = useMutation({
    mutationFn: () =>
      api.criar<{ id: string }>(
        substituindo ? `/acordos/${substituindo.id}/renegociar` : '/acordos',
        {
        ...(substituindo ? {} : { devedorId }),
        valorTotal,
        ...(valorEntrada.trim() ? { valorEntrada } : {}),
        quantidadeParcelas,
        dataPrimeiraParcela,
        ...(dataEntrada ? { dataEntrada } : {}),
        formaPagamento,
        percentualMulta: temMulta ? percentualMulta : null,
          ...(observacoes.trim() ? { observacoes: observacoes.trim() } : {}),
        },
      ),
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['devedor', devedorId] });
      void clienteConsulta.invalidateQueries({ queryKey: ['painel'] });
      void clienteConsulta.invalidateQueries({ queryKey: ['contagens'] });
      aoFechar();
    },
    onError: (e) => {
      if (e instanceof FalhaDaApi) {
        setErro(e.message);
        setInvalidos(e.camposInvalidos ?? {});
        return;
      }
      setErro('Não foi possível firmar o acordo.');
    },
  });

  function submeter(evento: FormEvent) {
    evento.preventDefault();
    const faltando: Record<string, string[]> = {};
    if (!valorTotal.trim()) faltando['valorTotal'] = ['Informe o valor acordado.'];
    if (!dataPrimeiraParcela) {
      faltando['dataPrimeiraParcela'] = ['Informe o primeiro vencimento.'];
    }
    setInvalidos(faltando);
    if (Object.keys(faltando).length > 0) {
      setErro(null);
      return;
    }
    firmar.mutate();
  }

  const erroDe = (campo: string) => {
    const mensagem = invalidos[campo]?.[0];
    return mensagem ? { erro: mensagem } : {};
  };

  return (
    <Modal
      aberto={aberto}
      aoFechar={aoFechar}
      titulo={substituindo ? 'Alterar o acordo' : 'Firmar acordo'}
      descricao={
        substituindo
          ? `${nomeDoDevedor} — substituindo o acordo de ${moeda(substituindo.valorTotal)}`
          : `${nomeDoDevedor} — dívida de origem ${moeda(valorSugerido)}`
      }
      largura="44rem"
      rodape={
        <>
          <Botao onClick={aoFechar} desabilitado={firmar.isPending}>
            Cancelar
          </Botao>
          <Botao
            tipo="primario"
            submeter
            formulario={ID_DO_FORMULARIO}
            desabilitado={firmar.isPending}
          >
            {firmar.isPending
              ? substituindo
                ? 'Salvando…'
                : 'Firmando…'
              : substituindo
                ? 'Salvar o novo acordo'
                : 'Firmar acordo'}
          </Botao>
        </>
      }
    >
      <form id={ID_DO_FORMULARIO} onSubmit={submeter} className="space-y-5">
        {erro && <Alerta tom="vencido">{erro}</Alerta>}

        {/* O QUE ACONTECE COM O ANTERIOR, dito antes de preencher qualquer campo.
            É a dúvida de quem clica: "e o que ela já pagou, some?". Não some. */}
        {substituindo && (
          <Alerta tom="atencao">
            O acordo de {moeda(substituindo.valorTotal)} passa a constar como{' '}
            <strong>renegociado</strong> e continua na ficha
            {substituindo.parcelasPagas > 0 ? (
              <>
                {' '}
                com {plural(substituindo.parcelasPagas, 'parcela paga', 'parcelas pagas')} —{' '}
                {moeda(substituindo.totalPago)} que a pessoa já quitou e não se perde de
                vista
              </>
            ) : (
              ', ainda sem nenhuma parcela paga'
            )}
            . As parcelas <strong>em aberto</strong> são canceladas, porque são
            reescritas aqui. O caso segue em acordo — isto não é rescisão e não leva ao
            judicial.
          </Alerta>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Campo
            rotulo="Valor acordado (R$)"
            valor={valorTotal}
            aoMudar={(v) => {
              setValorTotal(v);
              setErro(null);
            }}
            placeholder="1.850,00"
            auxilio={
              substituindo
                ? `Parcelas em aberto somam ${moeda(substituindo.saldoEmAberto)}. Some os juros da repactuação.`
                : 'O que ficou combinado, não a dívida.'
            }
            {...erroDe('valorTotal')}
          />
          <Campo
            rotulo="Entrada (R$)"
            valor={valorEntrada}
            aoMudar={setValorEntrada}
            placeholder="opcional"
            auxilio="Deixe vazio se não houver."
            {...erroDe('valorEntrada')}
          />
          <Campo
            rotulo="Parcelas"
            valor={quantidadeParcelas}
            aoMudar={setQuantidadeParcelas}
            placeholder="6"
            auxilio="Além da entrada."
            {...erroDe('quantidadeParcelas')}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Campo
            rotulo="Primeiro vencimento"
            tipo="date"
            valor={dataPrimeiraParcela}
            aoMudar={setDataPrimeiraParcela}
            {...erroDe('dataPrimeiraParcela')}
          />
          <Campo
            rotulo="Vencimento da entrada"
            tipo="date"
            valor={dataEntrada}
            aoMudar={setDataEntrada}
            {...erroDe('dataEntrada')}
          />
          <Seletor
            rotulo="Forma de pagamento"
            valor={formaPagamento}
            aoMudar={setFormaPagamento}
            opcoes={FORMAS}
          />
        </div>

        <fieldset className="space-y-3 border-t pt-4">
          <Caixa
            rotulo="O instrumento tem cláusula penal por descumprimento"
            marcado={temMulta}
            aoMudar={setTemMulta}
          />
          {temMulta && (
            <div className="sm:w-48">
              <Campo
                rotulo="Multa (%)"
                valor={percentualMulta}
                aoMudar={setPercentualMulta}
                placeholder="20"
                {...erroDe('percentualMulta')}
              />
            </div>
          )}
          <p className="text-xs text-[color:var(--texto-tenue)]">
            Sem marcar, o acordo fica <strong>sem multa</strong> — que é diferente de
            multa pactuada em zero. A projeção de rescisão na ficha usa este número.
          </p>
        </fieldset>

        {/* AS PARCELAS ANTES DE GRAVAR.
            Parcelamento tem arredondamento: R$ 1.000,00 em 3x não dá três parcelas
            iguais. Quem combina com o devedor precisa ver onde o centavo foi parar
            antes de prometer — e depois de gravado, mudar exige mexer no acordo. */}
        {simulacao && (
          <div className="rounded-md border">
            <div className="flex items-baseline justify-between border-b px-4 py-2.5">
              <span className="text-sm text-[color:var(--texto)]">Como vai ficar</span>
              {!simulacao.confere && (
                <span className="text-xs" style={{ color: 'var(--color-vencido)' }}>
                  a soma não fecha com o total
                </span>
              )}
            </div>
            <ul className="max-h-52 overflow-y-auto">
              {simulacao.parcelas.map((p) => (
                <li
                  key={p.numero}
                  className="flex items-center justify-between border-b px-4 py-1.5 text-sm last:border-0"
                >
                  <span className="text-[color:var(--texto-suave)]">
                    {p.ehEntrada ? 'Entrada' : `Parcela ${p.numero}`}
                  </span>
                  <span className="tabular">
                    {p.valorFormatado}
                    <span className="ml-3 text-xs text-[color:var(--texto-tenue)]">
                      {p.vencimentoFormatado}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        <AreaDeTexto
          rotulo="Observações (opcional)"
          valor={observacoes}
          aoMudar={setObservacoes}
          linhas={2}
          placeholder="Contexto da negociação."
        />
      </form>
    </Modal>
  );
}
